<script setup>
  import {computed, onBeforeMount, ref, watch} from "vue";

  import ComponentFullDateInput from "@components/Inputs/FullDateInput.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import ComponentTextarea from "@components/Textarea/Textarea.vue";
  import {additionalinformationDecreed} from "@pages/Records/Components/Result/Enums.js";
  import {checkAdditionalInformationDetailDialog} from "@pages/Records/Components/Sections/MedicationBar/Utils/medicationConsistencyCheck.js";
  import {MedCheckbox, MedFormField, MedIcon, MedInfoButton, MedLabel, MedLink} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";
  import {usePrivacyGuard} from "@utils/Helpers/PrivacyGuard.js";
  import {cloneDeep} from "es-toolkit";
  import {each} from "es-toolkit/compat";

  defineOptions({
    name: "MedicationComponentDetailDialogAdditionalData",
  });

  const props = defineProps({
    isEditable: {
      type: Boolean,
      default: false,
    },
    medicationForm: {
      type: Object,
      required: true,
    },
  });

  const emit = defineEmits(["valid", "invalid", "changeForm"]);

  const {validatePrivacyObject} = usePrivacyGuard();
  const showAdditionalData = ref(false);
  const bmpCheckbox = ref(false);
  const brownBagCheckbox = ref(false);
  const otherCheckbox = ref(false);
  const invalidPeriodError = ref(null);
  const invalidInfoError = ref(null);

  const additionalInformationForm = ref({
    decreed: props.medicationForm.value.additionalinformation?.decreed ?? null,
    requirements: props.medicationForm.value.additionalinformation?.requirements ?? null,
    since: props.medicationForm.value.additionalinformation?.since ?? null,
    source: props.medicationForm.value.additionalinformation?.source,
    sideeffects: props.medicationForm.value.additionalinformation?.sideeffects,
    to: props.medicationForm.value.additionalinformation?.to ?? null,
  });

  const dosage = ref({});

  const requirementOptions = {
    splitable: "Teilbar",
    mortarable: "Mörserbar",
    suspendable: "Suspendierbar",
  };

  watch(
    () => additionalInformationForm.value.source,
    (newSourceList) => {
      each(newSourceList, (source) => {
        switch (source) {
          case "bmp": {
            bmpCheckbox.value = true;

            break;
          }
          case "brownbag": {
            brownBagCheckbox.value = true;

            break;
          }
          case "other": {
            otherCheckbox.value = true;

            break;
          }
          // No default
        }
      });
    },
  );

  watch(
    () => bmpCheckbox.value,
    (newValue) => {
      if (newValue) {
        addToList("bmp");
      } else {
        removeFromList("bmp");
      }
    },
  );

  watch(
    () => brownBagCheckbox.value,
    (newValue) => {
      if (newValue) {
        addToList("brownbag");
      } else {
        removeFromList("brownbag");
      }
    },
  );

  watch(
    () => otherCheckbox.value,
    (newValue) => {
      if (newValue) {
        addToList("other");
      } else {
        removeFromList("other");
      }
    },
  );

  onBeforeMount(() => {
    dosage.value = cloneDeep(props.medicationForm.value.dosage);

    bmpCheckbox.value = additionalInformationForm.value.source.includes("bmp");
    brownBagCheckbox.value = additionalInformationForm.value.source.includes("brownbag");
    otherCheckbox.value = additionalInformationForm.value.source.includes("other");

    showAdditionalData.value = checkAdditionalInformationDetailDialog(props.medicationForm.value);
  });

  const addToList = (sourceName) => {
    if (!additionalInformationForm.value.source.includes(sourceName)) {
      additionalInformationForm.value.source.push(sourceName);
      emit("changeForm", {key: "additionalinformation.source", newValue: additionalInformationForm.value.source});
    }
  };

  const removeFromList = (sourceName) => {
    const indexFromList = additionalInformationForm.value.source.indexOf(sourceName);

    if (indexFromList !== -1) {
      additionalInformationForm.value.source.splice(indexFromList, 1);
      emit("changeForm", {key: "additionalinformation.source", newValue: additionalInformationForm.value.source});
    }
  };

  const handleDosageInfo = (newValue) => {
    invalidInfoError.value = null;

    const linebreaks = newValue?.match(/\n/g) || [];
    if (linebreaks?.length > 1) {
      invalidInfoError.value = "Bitte maximal ein Zeilenumbruch.";
    }

    if (invalidInfoError.value === null) {
      emit("valid", "invalidInfo");
      emit("changeForm", {key: "dosage.info", newValue: dosage.value.info});
    } else {
      emit("invalid", "invalidInfo");
    }
  };

  const handleDecreed = (newValue) => {
    additionalInformationForm.value.decreed = newValue;
    emit("changeForm", {key: "additionalinformation.decreed", newValue: additionalInformationForm.value.decreed});
  };

  const handleDateInput = (type, newDateValue) => {
    if (type === "dateSince") {
      additionalInformationForm.value.since = newDateValue;
    } else {
      additionalInformationForm.value.to = newDateValue;
    }

    const validPeriod = isSinceDateBeforeToDate(
      additionalInformationForm.value.to,
      additionalInformationForm.value.since,
    );

    if (validPeriod) {
      emit("valid", "invalidPeriod");
      invalidPeriodError.value = null;

      emit("changeForm", {key: "additionalinformation.since", newValue: additionalInformationForm.value.since});
      emit("changeForm", {key: "additionalinformation.to", newValue: additionalInformationForm.value.to});
    } else {
      invalidPeriodError.value = "Ungültiger Zeitraum.";
      emit("invalid", "invalidPeriod");
    }
  };

  function isSinceDateBeforeToDate(toDate, sinceDate) {
    const toDateTimestamp = new Date(toDate).getTime();
    const sinceDateTimestamp = new Date(sinceDate).getTime();

    if (toDate === null || sinceDate === null) {
      return true;
    }

    return toDateTimestamp > sinceDateTimestamp;
  }

  const handleRequirements = (newValue) => {
    additionalInformationForm.value.requirements = newValue;

    emit("changeForm", {
      key: "additionalinformation.requirements",
      newValue: additionalInformationForm.value.requirements,
    });
  };

  const privacyErrors = computed(() => {
    return validatePrivacyObject(dosage, {info: "Hinweise zur Medikation"});
  });
</script>

<template>
  <div>
    <button
      class="accordion-header flex w-full cursor-pointer border-gray-200 bg-white bg-[linear-gradient(0deg,#fff,#f0f0f0)] py-3 text-left text-base text-gray-800"
      :class="{'border-b': !showAdditionalData}"
      :data-test="getTestId('additional-accordion-header')"
      @click.prevent="showAdditionalData = !showAdditionalData"
    >
      <div class="flex items-center font-medium">
        <MedIcon
          size="lg"
          icon="keyboard_arrow_down"
          :class="
            cn('transition-transform duration-200', {
              'rotate-180': showAdditionalData,
            })
          "
        />

        <div class="flex items-center space-x-1">
          <span>Weitere Informationen</span>

          <span @click.stop @mousedown.stop>
            <MedInfoButton
              size="lg"
              side="right"
              aria-label="Weitere Informationen"
              disable-portal
              :data-test="getTestId('additional-data-info-button')"
            >
              <p class="text-sm">z.B. Einnahmehinweise, Verordner, Quelle, Teilbarkeit etc.</p>
            </MedInfoButton>
          </span>
        </div>
      </div>
    </button>

    <div v-if="showAdditionalData" class="grid grid-cols-12 gap-x-2 gap-y-4 p-2">
      <!-- dosageInfo -->
      <div class="col-span-9 col-start-1">
        <ComponentTextarea
          v-model="dosage.info"
          :disabled="!isEditable"
          label="Hinweise zur Medikation (wird in Medikationsplan übernommen)"
          maxlength="80"
          rows="2"
          :validation="invalidInfoError ?? medicationForm?.errors['dosage.info'] ?? null"
          :privacy-guard="privacyErrors.info ?? undefined"
          helper-text="Darf maximal einen Zeilenumbruch beinhalten."
          :test-id="getTestId('additional-info')"
          @input="handleDosageInfo($event)"
        />
      </div>

      <!-- additional.source -->
      <fieldset class="col-span-9 col-start-1">
        <legend class="text-sm text-gray-500">Vorhanden in</legend>
        <div class="mt-2 grid grid-cols-3 gap-x-2 gap-y-4">
          <MedFormField orientation="horizontal">
            <MedCheckbox
              v-model="bmpCheckbox"
              :disabled="!isEditable"
              variant="accent"
              :data-test="getTestId('additional-bmp-checkbox')"
            />

            <MedLabel>Medikationsplan / -datei</MedLabel>
          </MedFormField>

          <MedFormField orientation="horizontal">
            <MedCheckbox
              v-model="brownBagCheckbox"
              :disabled="!isEditable"
              variant="accent"
              :data-test="getTestId('additional-brown-bag-checkbox')"
            />

            <MedLabel>Brown Bag</MedLabel>
          </MedFormField>

          <MedFormField orientation="horizontal">
            <MedCheckbox
              v-model="otherCheckbox"
              :disabled="!isEditable"
              variant="accent"
              :data-test="getTestId('additional-other-checkbox')"
            />

            <MedLabel>Andere Datenquelle</MedLabel>
          </MedFormField>
        </div>
      </fieldset>

      <!-- additional.decreed -->
      <div class="col-span-3 col-start-1 mt-1 whitespace-nowrap">
        <ComponentSelect
          :model-value="additionalInformationForm.decreed"
          label="Verordnet durch / Herkunft"
          :nullable="true"
          :disabled="!isEditable"
          :options="additionalinformationDecreed"
          :test-id="getTestId('additional-decreed')"
          @change="handleDecreed"
        />
      </div>

      <!-- additional.since -->
      <ComponentFullDateInput
        ref="since"
        v-model="additionalInformationForm.since"
        class="col-span-3 mt-1"
        label="Anw. seit"
        :disabled="!isEditable"
        :test-id="getTestId('additional-since')"
        @input="handleDateInput('dateSince', $event)"
      />

      <!-- additional.to -->
      <ComponentFullDateInput
        v-model="additionalInformationForm.to"
        :validation="invalidPeriodError"
        class="col-span-3 mt-1"
        label="Anw. bis"
        :disabled="!isEditable"
        :test-id="getTestId('additional-to')"
        @input="handleDateInput('dateTo', $event)"
      />

      <!-- additional.requirements -->
      <ComponentSelect
        :id="'requirements'"
        ref="requirements"
        :model-value="additionalInformationForm.requirements"
        :name="'requirements'"
        :nullable="true"
        :options="requirementOptions"
        class="col-span-3 col-start-1"
        label="Anforderungen Arzneiform"
        :disabled="!isEditable"
        :test-id="getTestId('additional-requirements')"
        @change="handleRequirements"
      />

      <p class="col-span-12 col-start-1">
        Das Merkmal „Sonde“ kann unter den Patientenangaben als

        <MedLink
          variant="primary"
          icon="launch"
          icon-align="end"
          target="_blank"
          :href="$route('patients.edit', {id: $page.props.patient.id}) + '#patient-attributes-data'"
          :data-test="getTestId('edit-patient-attributes-link')"
        >
          individuelle Besonderheit
        </MedLink>

        hinterlegt werden.
      </p>
    </div>
  </div>
</template>
